import { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { getSession, updateSessionNote } from "../lib/api";
import { fmtAbsolute, fmtDuration } from "../lib/format";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function SessionDetailSheet({ projectId, sessionId, open, onOpenChange, onSaved }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(false);
  const [note, setNote] = useState("");

  useEffect(() => {
    if (!open || !projectId || !sessionId) return;
    setLoading(true);
    getSession(projectId, sessionId)
      .then((s) => {
        setSession(s);
        setNote(s.session_note || "");
      })
      .catch(() => toast.error("Could not load session"))
      .finally(() => setLoading(false));
  }, [projectId, sessionId, open]);

  const save = async () => {
    if (!session) return;
    try {
      await updateSessionNote(projectId, sessionId, note);
      toast.success("Note saved");
      onSaved?.();
    } catch {
      toast.error("Failed to save");
    }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="bg-white border-l border-[#e5e7eb] w-full sm:max-w-lg overflow-y-auto"
        data-testid="session-sheet"
      >
        <SheetHeader>
          <SheetTitle className="text-[#111827]">Session detail</SheetTitle>
        </SheetHeader>
        {loading || !session ? (
          <div className="py-10 flex items-center justify-center text-[#6b7280]">
            <Loader2 className="animate-spin" size={18} />
          </div>
        ) : (
          <div className="mt-6 space-y-6">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="rounded-lg bg-[#f7f8fa] border border-[#e5e7eb] p-3">
                <div className="text-[10px] uppercase tracking-wider text-[#6b7280]">What changed</div>
                <p className="text-sm text-[#111827] mt-1 leading-relaxed">{session.summary}</p>
              </div>
              <div className="rounded-lg bg-[#f7f8fa] border border-[#e5e7eb] p-3">
                <div className="text-[10px] uppercase tracking-wider text-[#6b7280]">Likely next step</div>
                <p className="text-sm text-[#111827] mt-1 leading-relaxed">
                  {session.suggested_next_step}
                </p>
              </div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6b7280]">Area of work</div>
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="sheet-areas">
                {areaOfWork(session.files_touched || []).map((a) => (
                  <span
                    key={a}
                    className="text-[11px] px-2 py-0.5 rounded-full bg-white border border-[#e5e7eb] text-[#374151]"
                  >
                    {a}
                  </span>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <Mini label="Duration" value={fmtDuration(session.duration_seconds)} />
              <Mini label="Files" value={session.files_touched_count} />
              <Mini label="Commits" value={(session.commit_shas || []).length} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6b7280]">When</div>
              <p className="text-sm text-[#111827] mt-1 font-mono">
                {fmtAbsolute(session.start_time)} → {fmtAbsolute(session.end_time)}
              </p>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6b7280]">
                What were you trying to finish?
              </div>
              <textarea
                data-testid="sheet-note-input"
                rows={2}
                className="mt-2 w-full text-sm bg-[#f7f8fa] border border-[#e5e7eb] rounded-md px-3 py-2 outline-none focus:border-indigo-400 transition"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a short note to remember this session"
              />
              <button
                data-testid="sheet-save-note"
                onClick={save}
                className="mt-2 px-3 py-1.5 rounded-md bg-[#111827] text-white text-xs hover:bg-[#374151] transition"
              >
                Save note
              </button>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-[#6b7280] mb-2">
                Files touched
              </div>
              <ul className="space-y-1.5 max-h-72 overflow-y-auto pr-1">
                {(session.files_touched || []).map((f) => (
                  <li
                    key={f}
                    className="font-mono text-[12px] text-[#374151] truncate border border-[#e5e7eb] rounded px-2 py-1 bg-[#f7f8fa]"
                    title={f}
                  >
                    {f}
                  </li>
                ))}
              </ul>
            </div>
            {session.events?.length > 0 && (
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[#6b7280] mb-2">
                  Activity
                </div>
                <ul className="space-y-1 max-h-72 overflow-y-auto pr-1">
                  {session.events.map((e) => (
                    <li
                      key={e.id}
                      className="text-[12px] text-[#374151] flex items-center justify-between gap-3 border-b border-dashed border-[#e5e7eb] py-1"
                    >
                      <span className="truncate">
                        <span className="text-[#9ca3af] mr-2">[{e.event_type}]</span>
                        <span className="font-mono">{e.file_path}</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Mini({ label, value }) {
  return (
    <div className="rounded-md bg-[#f7f8fa] border border-[#e5e7eb] p-3">
      <div className="text-[10px] uppercase tracking-wider text-[#6b7280]">{label}</div>
      <div className="text-base font-medium text-[#111827] mt-1">{value}</div>
    </div>
  );
}

function areaOfWork(files) {
  const labels = new Set();
  for (const f of files) {
    const p = (f || "").toLowerCase();
    if (/\.(tsx|jsx|vue|svelte)$/.test(p) || p.includes("/frontend/") || p.includes("/src/components")) labels.add("Frontend");
    if (/\.(py|rb|go|java|rs)$/.test(p) || p.includes("/backend/") || p.includes("/api/") || p.includes("/server/")) labels.add("Backend");
    if (/\.(css|scss|sass|less)$/.test(p)) labels.add("Styling");
    if (/\.(md|mdx|rst|txt)$/.test(p) || p.includes("/docs/") || p.endsWith("readme.md")) labels.add("Docs");
    if (p.includes("/assets/") || /\.(png|jpg|jpeg|svg|gif|webp)$/.test(p)) labels.add("Assets");
    if (/(payment|checkout|stripe|webhook)/.test(p)) labels.add("Payment flow");
    if (/(auth|login|signup|session)/.test(p)) labels.add("Auth flow");
    if (/(\.test\.|\.spec\.|__tests__)/.test(p)) labels.add("Tests");
    if (/\.(json|yaml|yml|toml|env)$/.test(p)) labels.add("Config");
  }
  return [...labels].slice(0, 5);
}
